import { FunctionComponent, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useGetLiftOperatorProductsQuery } from '@starjack/entities/liftoperator';
import { useAuthentication } from '@/hooks/useAuthentication';
import { SectionTitle } from './SectionTitle';
import { ConfigurationGrid } from './ConfigurationGrid';
import { PersonAccordion } from './PersonAccordion';

type ProductConfigurationDialogProps = {
    shortProductId: string;
};

export const ProductConfigurationDetailsDialog: FunctionComponent<
    ProductConfigurationDialogProps
> = ({ shortProductId }) => {
    const [open, setOpen] = useState(false);
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();

    const { data: products, isLoading } = useGetLiftOperatorProductsQuery(
        { liftOperatorId },
        { skip: !open }
    );

    const product = products?.find((p) => p.shortProductId === shortProductId);
    const groupedPersons = product?.persons;

    return (
        <>
            <Button variant='outlined' onClick={() => setOpen(true)}>
                Konfigurationsdetails ansehen
            </Button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth='md'
                fullWidth
            >
                <DialogTitle>
                    {product?.name?.de ?? 'Konfiguration'}
                    {product?.name?.en && (
                        <Typography variant='body2' color='text.secondary'>
                            {product.name.en}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {isLoading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {product && (
                        <Stack spacing={3}>
                            {/* General Info */}
                            <Box>
                                <SectionTitle>
                                    Allgemeine Informationen
                                </SectionTitle>
                                <Stack
                                    direction='row'
                                    spacing={1}
                                    flexWrap='wrap'
                                    useFlexGap
                                >
                                    <Chip
                                        label={`Typ: ${product.type}`}
                                        size='small'
                                        variant='outlined'
                                    />
                                    <Chip
                                        label={`Dauer: ${product.duration} ${product.duration === 1 ? 'Tag' : 'Tage'}`}
                                        size='small'
                                        variant='outlined'
                                    />
                                    <Chip
                                        label={`Foto: ${product.photoRequired ? 'Ja' : 'Nein'}`}
                                        size='small'
                                        variant='outlined'
                                    />
                                    <Chip
                                        label={`Sortierung: ${product.sortKey}`}
                                        size='small'
                                        variant='outlined'
                                    />
                                </Stack>
                            </Box>

                            {/* Persons & Prices */}
                            <Box>
                                <SectionTitle>
                                    Personenkategorien & Preise
                                </SectionTitle>

                                <Box>
                                    {groupedPersons.map((person, idx) => (
                                        <PersonAccordion
                                            key={idx}
                                            person={person}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {/* Configurations */}
                            <Box>
                                <SectionTitle>Buchungsoptionen</SectionTitle>

                                <ConfigurationGrid
                                    configurations={[
                                        {
                                            label: 'Portal',
                                            config: product.portalConfiguration,
                                        },
                                        {
                                            label: 'Integration',
                                            config: product.integrationConfiguration,
                                        },
                                        {
                                            label: 'B2B',
                                            config: product.b2bConfiguration,
                                        },
                                        {
                                            label: 'Hospitality',
                                            config: product.hospitalityConfiguration,
                                        },
                                        {
                                            label: 'Mobile',
                                            config: product.mobileConfiguration,
                                        },
                                    ]}
                                />
                            </Box>
                        </Stack>
                    )}

                    {!isLoading && !product && (
                        <Typography color='text.secondary'>
                            Keine Produktdaten gefunden.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
